import { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";

import { useDebounce } from "../../../../hooks/useDebounce";
import { Transaction, TransactionChannelMap, TransactionStatusMap, VendingStatusMap, vendingStatusClassName } from "../../../../model/transaction";
import { Range } from "react-date-range";
import {
	useLazyGetPrepaidTransactionsForUtilityAdminQuery,
	useLazyExportPrepaidTransactionsForUtilityAdminQuery,
} from "../../../../service/transaction-service";
import { getErrorMessage } from "../../../../utils/error-handler";
import { toast } from "react-toastify";
import styles from "../../utilityAdmin.module.scss";
import { formatCurrency, formatTimestamp } from "../../../../utils";
import { EyeIcon, FilterIcon2, VendingActiveIcon } from "../../../../assets/icons";
import CommonSideDialog from "../../../../common/components/side-dialog/CommonSideDialog";
import { FilterItem } from "../../../../common/components/xepp/table-filter/table-filter.type";
import DataTable, { ColumnType } from "../../../../common/components/xepp/table/DataTable";
import Button from "../../../../common/components/xepp/button/Button";
import SearchInput from "../../../../common/components/xepp/search-input/SearchInput";
import Filter from "../../../../common/components/xepp/table-filter/table-filter-components";
import TransactionDetailsContainer from "../../../../common/components/xepp/transaction-details/TransactionDetailsContainer";
import MapRefundTokenCell from "../../../../common/components/xepp/map-refund-token-cell/MapRefundTokenCell";
import EmptyState from "../../../../common/components/xepp/empty-state/EmptyState";
import { TablePagination } from "../../../../common/components/xepp/table-pagination/TablePagination";
import { hasPermission } from "../../../../guards/common/authentification-functions";
import { UtilityPermissions } from "../../../../model/permissions/permissions";
import { useConfigLayout } from "../../../../hooks/useConfigLayout";

const formatLocalDate = (date: Date): string => {
	const pad = (n: number) => String(n).padStart(2, "0");
	return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

export const VendingHistory = () => {
	const [searchParams, setSearchParams] = useSearchParams();

	const now = new Date();
	const startDate = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
	const defaultFrom = formatLocalDate(startDate);
	const defaultTo = formatLocalDate(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1));

	const [searchTerm, setSearchTerm] = useState<string>(searchParams.get("search") ?? "");
	const debouncedSearchTerm = useDebounce(searchTerm, 500);
	const [isOpen, setIsOpen] = useState(false);
	const [selectedReference, setSelectedReference] = useState<string>("");
	const [Page, setPage] = useState(Number(searchParams.get("page")) || 1);
	const PageSize = 15;

	// Filter states
	const [isFilterOpen, setIsFilterOpen] = useState(false);
	const [selectedTransactionStatus, setSelectedTransactionStatus] = useState<string>(searchParams.get("transactionStatus") ?? "");
	const [selectedVendingStatus, setSelectedVendingStatus] = useState<string>(searchParams.get("vendingStatus") ?? "");
	const [selectedTransactionType, setSelectedTransactionType] = useState<string>(searchParams.get("transactionType") ?? "");
	const [selectedMapRefundToken, setSelectedMapRefundToken] = useState<string>(searchParams.get("hasMapRefundToken") ?? "");
	const [dateRange, setDateRange] = useState<Range>({
		startDate: searchParams.get("from") ? new Date(searchParams.get("from")!) : startDate,
		endDate: searchParams.get("to") ? new Date(new Date(searchParams.get("to")!).getTime() - 24 * 60 * 60 * 1000) : now,
		key: "selection",
	});
	const [appliedFilters, setAppliedFilters] = useState<{
		TransactionStatus: string;
		VendingStatus: string;
		TransactionType: string;
		HasMapRefundToken: string;
		From?: string;
		To?: string;
	}>({
		TransactionStatus: searchParams.get("transactionStatus") ?? "",
		VendingStatus: searchParams.get("vendingStatus") ?? "",
		TransactionType: searchParams.get("transactionType") ?? "",
		HasMapRefundToken: searchParams.get("hasMapRefundToken") ?? "",
		From: searchParams.get("from") ?? defaultFrom,
		To: searchParams.get("to") ?? defaultTo,
	});

	const { updateLayout } = useConfigLayout();
	const [getPrepaidTransactions, { data, isLoading, isFetching }] = useLazyGetPrepaidTransactionsForUtilityAdminQuery();
	const [exportPrepaidTransactions] = useLazyExportPrepaidTransactionsForUtilityAdminQuery();

	const appliedFiltersRef = useRef(appliedFilters);
	const searchTermRef = useRef(debouncedSearchTerm);
	useEffect(() => {
		appliedFiltersRef.current = appliedFilters;
	}, [appliedFilters]);
	useEffect(() => {
		searchTermRef.current = debouncedSearchTerm;
	}, [debouncedSearchTerm]);

	const { count } = data ?? {};

	useEffect(() => {
		const filterParams = {
			Page,
			PageSize,
			...(appliedFilters.TransactionStatus && { TransactionStatus: appliedFilters.TransactionStatus }),
			...(appliedFilters.VendingStatus && { VendingStatus: appliedFilters.VendingStatus }),
			...(appliedFilters.HasMapRefundToken !== "" && { HasMapRefundToken: appliedFilters.HasMapRefundToken === "true" }),
			...(appliedFilters.TransactionType && { TransactionType: appliedFilters.TransactionType }),
			...(appliedFilters.From && { DateFrom: appliedFilters.From }),
			...(appliedFilters.To && { DateTo: appliedFilters.To }),
			...(debouncedSearchTerm && { Search: debouncedSearchTerm }),
		};

		getPrepaidTransactions(filterParams)
			.unwrap()
			.catch(err => getErrorMessage(err.data));
	}, [Page, PageSize, appliedFilters, debouncedSearchTerm, getPrepaidTransactions]);

	useEffect(() => {
		const params: Record<string, string> = {};
		if (Page > 1) params.page = String(Page);
		if (debouncedSearchTerm) params.search = debouncedSearchTerm;
		if (appliedFilters.TransactionStatus) params.transactionStatus = appliedFilters.TransactionStatus;
		if (appliedFilters.VendingStatus) params.vendingStatus = appliedFilters.VendingStatus;
		if (appliedFilters.TransactionType) params.transactionType = appliedFilters.TransactionType;
		if (appliedFilters.HasMapRefundToken !== "") params.hasMapRefundToken = appliedFilters.HasMapRefundToken;
		if (appliedFilters.From) params.from = appliedFilters.From;
		if (appliedFilters.To) params.to = appliedFilters.To;
		setSearchParams(params, { replace: true });
	}, [Page, debouncedSearchTerm, appliedFilters, setSearchParams]);

	useEffect(() => {
		if (searchTerm !== debouncedSearchTerm && searchTerm !== "") {
			setPage(1);
		}
	}, [searchTerm, debouncedSearchTerm]);

	const handleExport = useCallback(() => {
		const filters = appliedFiltersRef.current;
		const search = searchTermRef.current;
		const exportParams = {
			Page: 1,
			PageSize: 0,
			...(filters.TransactionStatus && { TransactionStatus: filters.TransactionStatus }),
			...(filters.VendingStatus && { VendingStatus: filters.VendingStatus }),
			...(filters.HasMapRefundToken !== "" && { HasMapRefundToken: filters.HasMapRefundToken === "true" }),
			...(filters.TransactionType && { TransactionType: filters.TransactionType }),
			...(filters.From && { DateFrom: filters.From }),
			...(filters.To && { DateTo: filters.To }),
			...(search && { Search: search }),
		};
		toast.info("Preparing export...");
		exportPrepaidTransactions(exportParams)
			.unwrap()
			.then(blob => {
				const url = URL.createObjectURL(blob);
				const link = document.createElement("a");
				link.href = url;
				link.download = `prepaid-transactions-${new Date().toISOString().slice(0, 10)}.csv`;
				document.body.appendChild(link);
				link.click();
				document.body.removeChild(link);
				URL.revokeObjectURL(url);
			})
			.catch((err: any) => {
				toast.error(getErrorMessage(err?.data));
			});
	}, [exportPrepaidTransactions]);

	useEffect(() => {
		updateLayout({ exportHandler: handleExport });
		return () => {
			updateLayout({ exportHandler: null });
		};
	}, [handleExport, updateLayout]);

	const handleViewTransaction = (transaction: Transaction) => {
		if (!hasPermission(UtilityPermissions.ViewPrepaidTransactions)) {
			toast.warning("You do not have the permission for this action");
			return;
		}

		setSelectedReference(transaction.reference);
		setIsOpen(true);
	};

	const handleOpenFilter = () => {
		setIsFilterOpen(true);
	};

	const handleCloseFilter = () => {
		setIsFilterOpen(false);
	};

	const handleApplyFilter = () => {
		let endDate = dateRange.endDate;
		if (dateRange.startDate && endDate && dateRange.startDate.toDateString() === endDate.toDateString()) {
			endDate = new Date(endDate);
			endDate.setDate(endDate.getDate() + 1);
		}
		setAppliedFilters({
			TransactionStatus: selectedTransactionStatus,
			VendingStatus: selectedVendingStatus,
			TransactionType: selectedTransactionType,
			HasMapRefundToken: selectedMapRefundToken,
			From: dateRange.startDate ? formatLocalDate(dateRange.startDate) : undefined,
			To: endDate ? formatLocalDate(new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate() + 1)) : undefined,
		});

		setIsFilterOpen(false);
		setPage(1);
	};

	const handleResetFilter = () => {
		setSelectedTransactionStatus("");
		setSelectedVendingStatus("");
		setSelectedTransactionType("");
		setSelectedMapRefundToken("");
		setIsFilterOpen(false);

		setDateRange({
			startDate: startDate,
			endDate: now,
			key: "selection",
		});

		setAppliedFilters({
			TransactionStatus: "",
			VendingStatus: "",
			TransactionType: "",
			HasMapRefundToken: "",
			From: formatLocalDate(startDate),
			To: formatLocalDate(new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1)),
		});

		setPage(1);
	};

	const handleTransactionStatusChange = (value: string) => {
		setSelectedTransactionStatus(value);
	};

	const handleVendingStatusChange = (value: string) => {
		setSelectedVendingStatus(value);
	};

	const handleTransactionTypeChange = (value: string) => {
		setSelectedTransactionType(value);
	};

	const handleMapRefundTokenChange = (value: string) => {
		setSelectedMapRefundToken(value);
	};

	const handleDateRangeChange = (ranges: any) => {
		setDateRange(ranges.selection);
	};

	const filterItems: FilterItem[] = [
		{
			id: "date_range",
			label: "DATE RANGE",
			type: "date",
			initialDateRange: dateRange,
			onChange: handleDateRangeChange,
		},
		{
			id: "transaction_status",
			label: "PAYMENT STATUS",
			type: "radio",
			options: [
				{
					id: "completed",
					label: "Completed",
					value: "Completed",
					isSelected: selectedTransactionStatus === "Completed",
				},
				{
					id: "failed",
					label: "Failed",
					value: "Failed",
					isSelected: selectedTransactionStatus === "Failed",
				},
				{
					id: "in-progress",
					label: "In-Progress",
					value: "InProgress",
					isSelected: selectedTransactionStatus === "InProgress",
				},
			],
			onChange: handleTransactionStatusChange,
		},
		{
			id: "vending_status",
			label: "VENDING STATUS",
			type: "radio",
			options: [
				{
					id: "vending_completed",
					label: "Completed",
					value: "Completed",
					isSelected: selectedVendingStatus === "Completed",
				},
				{
					id: "vending_failed",
					label: "Failed",
					value: "Failed",
					isSelected: selectedVendingStatus === "Failed",
				},
				{
					id: "vending_in-progress",
					label: "In-Progress",
					value: "InProgress",
					isSelected: selectedVendingStatus === "InProgress",
				},
				{
					id: "vending_failed_with_refund",
					label: "Failed with Refund",
					value: "FailedWithRefund",
					isSelected: selectedVendingStatus === "FailedWithRefund",
				},
			],
			onChange: handleVendingStatusChange,
		},
		{
			id: "map_refund_token",
			label: "MAP REFUND TOKEN",
			type: "radio",
			options: [
				{
					id: "map_refund_with",
					label: "With MAP Refund Token",
					value: "true",
					isSelected: selectedMapRefundToken === "true",
				},
				{
					id: "map_refund_without",
					label: "Without MAP Refund Token",
					value: "false",
					isSelected: selectedMapRefundToken === "false",
				},
			],
			onChange: handleMapRefundTokenChange,
		},
		{
			id: "transactionType",
			label: "CHANNEL",
			type: "radio",
			options: [
				{
					id: "paymentHub",
					label: "Payment Hub",
					value: "PaymentHub",
					isSelected: selectedTransactionType === "PaymentHub",
				},
				{
					id: "CashPayment",
					label: "Cash Payment",
					value: "CashPayment",
					isSelected: selectedTransactionType === "CashPayment",
				},
			],
			onChange: handleTransactionTypeChange,
		},
	];

	const columns: Array<ColumnType<Transaction>> = [
		{
			header: "Date & Time",
			accessor: "createdAt",
			render: (record: Transaction) => <span>{formatTimestamp(record.createdAt ?? "")}</span>,
		},
		{
			accessor: "user",
			header: "Name of Customer",
			render: (record: Transaction) => (
				<span>
					{record.user.firstName} {record.user.lastName}
				</span>
			),
		},
		{
			accessor: "reference",
			header: "Reference",
			render: (record: Transaction) => (
				<span title={record.reference} className={styles.ellipsisText}>
					{record.reference}
				</span>
			),
		},
		{
			accessor: "amount.amount",
			header: "Amount Paid",
			render: (record: Transaction) => <span>{record.amount.amount ? formatCurrency(record.amount.amount, { currency: record.amount.currency }) : "N/A"}</span>,
		},
		{
			accessor: "type",
			header: "Channel",
			render: (record: Transaction) => <span className={styles.channelBadge}>{TransactionChannelMap[record.type]}</span>,
		},
		{
			accessor: "status",
			header: "Payment Status",
			render: (record: Transaction) => (
				<span
					className={`${styles.statusIndicator} ${record.status && TransactionStatusMap[record.status] ? styles[TransactionStatusMap[record.status].toLowerCase()] : ""}`}
				>
					{record.status && TransactionStatusMap[record.status] ? TransactionStatusMap[record.status] : "N/A"}
				</span>
			),
		},
		{
			accessor: "vendingDetails.vendingToken.vendingStatus",
			header: "Vending Status",
			render: (record: Transaction) => {
				const vendingStatus = record.vendingDetails?.vendingToken?.vendingStatus ?? 0;
				return (
					<span className={`${styles.statusIndicator} ${styles[vendingStatusClassName(vendingStatus)] ?? ""}`}>
						{VendingStatusMap[vendingStatus] ?? "Unknown"}
					</span>
				);
			},
		},
		{
			accessor: "vendingDetails.vendingToken.mapRefundToken",
			header: "MAP Refund Token",
			render: (record: Transaction) => <MapRefundTokenCell token={record.vendingDetails?.vendingToken?.mapRefundToken} />,
		},
		{
			accessor: "district",
			header: "District",
			render: (record: Transaction) => (
				<span>
					{record.vendingDetails?.district ?? "N/A"} 
				</span>
			),
		},
		
		{
			accessor: "id",
			header: "",
			render: (record: Transaction) => (
				<Button leftIcon={<EyeIcon className={styles.icon} />} variant="text" text="View" onClick={() => handleViewTransaction(record)} />
			),
		},
	];

	// Header actions component
	const headerActions = (
		<>
			<SearchInput initialValue={searchTerm} onSearch={(value: string) => setSearchTerm(value)} placeholder="Search by reference..." />
			<Button leftIcon={<FilterIcon2 className={styles.icon} />} text="Add Filter" variant="outline" type="button" onClick={handleOpenFilter} />
		</>
	);

	return (
		<div className={styles.historyContainer}>
			<Filter filterItems={filterItems} onApplyFilter={handleApplyFilter} onResetFilter={handleResetFilter} onClose={handleCloseFilter} isOpen={isFilterOpen} />
			{isOpen && (
				<CommonSideDialog onCloseDialog={() => setIsOpen(!isOpen)}>
					<TransactionDetailsContainer reference={selectedReference} transactionType="vending" onClose={() => setIsOpen(false)} />
				</CommonSideDialog>
			)}
			<DataTable<Transaction>
				data={data?.items ?? []}
				loading={isLoading}
				isUpdating={isFetching}
				columns={columns}
				headerActions={headerActions}
				emptyComponent={<EmptyState icon={<VendingActiveIcon />} title="No vending history data" />}
			/>
			<TablePagination isLoading={isFetching} totalItems={count ?? 0} itemsPerPage={PageSize} currentPage={Page} onPageChange={setPage} />
		</div>
	);
};
